import { useRef, useState } from 'react'
import Turnstile, { type TurnstileHandvat } from '../components/Turnstile'
import { turnstileSitekey } from '../lib/turnstile'
import { FormulierAfgekeurd, verstuurContact } from '../lib/api'
import {
  contactVeldvolgorde,
  maximaleBerichtlengte,
  valideerContact,
  type ContactFormulierWaarden,
  type ContactFouten,
} from '../lib/contactvalidatie'

/*
 * P4 — Contact. Tekst en opbouw letterlijk uit legacy/current/contact.html
 * (CR-07, FR-19).
 *
 * Het formulier stuurt naar `/api/contact`, dat het bericht doormailt naar de
 * interne mailbox (FR-20). Er wordt niets opgeslagen: een contactbericht is geen
 * aanvraag, en wat er niet ligt hoeft ook niet bewaard of opgeruimd te worden.
 *
 * De bevestiging staat op het scherm en gaat niet per mail. Een bevestigingsmail
 * naar het ingevulde adres zou dit formulier tot verzendmachine maken voor wie het
 * adres van een ander invult.
 */

const nietAllesIngevuld =
  'Nog niet alles is goed ingevuld. Kijk de rood gemarkeerde velden na.'

export default function Contact() {
  const turnstile = useRef<TurnstileHandvat | null>(null)
  const turnstileToken = useRef('')

  const [fouten, setFouten] = useState<ContactFouten>({})
  const [melding, setMelding] = useState('')
  const [bezig, setBezig] = useState(false)
  const [verzonden, setVerzonden] = useState(false)

  function wisFout(veld: keyof ContactFormulierWaarden) {
    setFouten((huidig) => {
      if (!(veld in huidig)) return huidig

      const rest = { ...huidig }
      delete rest[veld]
      return rest
    })
  }

  /** Fout weghalen zodra het veld goed staat, maar er geen nieuwe neerzetten. */
  function bijInvoer(gebeurtenis: React.FormEvent<HTMLFormElement>) {
    const veld = (gebeurtenis.target as HTMLInputElement).name
    if (veld !== 'email' && veld !== 'bericht') return
    if (!(veld in fouten)) return

    if (!valideerContact(leesWaarden(gebeurtenis.currentTarget))[veld]) {
      wisFout(veld)
    }
  }

  async function bijVerzenden(gebeurtenis: React.FormEvent<HTMLFormElement>) {
    gebeurtenis.preventDefault()
    if (bezig) return

    const form = gebeurtenis.currentTarget
    const waarden = leesWaarden(form)
    const gevonden = valideerContact(waarden)

    if (Object.keys(gevonden).length > 0) {
      toonFouten(form, gevonden, nietAllesIngevuld)
      return
    }

    setFouten({})
    setMelding('')
    setBezig(true)

    try {
      await verstuurContact({
        ...waarden,
        website: leesVeld(form, 'website'),
        turnstileToken: turnstileToken.current,
      })

      setVerzonden(true)
    } catch (fout: unknown) {
      // Het token van Turnstile is eenmalig; zonder een vers exemplaar strandt
      // een tweede poging op iets wat de bezoeker niet kan zien.
      turnstile.current?.herstart()

      if (fout instanceof FormulierAfgekeurd) {
        const perVeld: ContactFouten = {}
        const overig: string[] = []

        for (const { veld, melding: tekst } of fout.fouten) {
          if (veld === 'email' || veld === 'bericht') {
            perVeld[veld] = tekst
          } else {
            overig.push(tekst)
          }
        }

        toonFouten(
          form,
          perVeld,
          overig.length > 0 ? overig.join(' ') : nietAllesIngevuld,
        )
      } else {
        setMelding(
          'Het lukt nu even niet om uw bericht te versturen. Probeer het zo ' +
            'nog eens, of mail ons rechtstreeks op info@gpkeuringen.nl.',
        )
      }

      setBezig(false)
    }
  }

  function toonFouten(
    form: HTMLFormElement,
    gevonden: ContactFouten,
    tekst: string,
  ) {
    setFouten(gevonden)
    setMelding(tekst)

    const eerste = contactVeldvolgorde.find((veld) => gevonden[veld])
    if (!eerste) return

    const element = form.elements.namedItem(eerste)
    if (element instanceof HTMLElement) element.focus()
  }

  return (
    <>
      <div className="page-head">
        <div className="wrap reveal">
          <span className="eyebrow">Contact</span>
          <h1>Wij helpen u graag</h1>
          <p className="lede">
            Mail ons op{' '}
            <a href="mailto:info@gpkeuringen.nl">
              <strong>info@gpkeuringen.nl</strong>
            </a>{' '}
            of gebruik het formulier hieronder. U krijgt zo snel mogelijk
            antwoord.
          </p>
        </div>
      </div>

      <section className="section section--contact">
        <div className="wrap">
          {verzonden ? (
            /*
              De bevestiging op het scherm die FR-20 vraagt. Het formulier gaat
              weg: het staat er niet meer om nog eens op te drukken, en wat
              iemand net heeft gestuurd hoeft hij niet nog een keer te lezen.

              Géén `reveal` op deze kaart. Die klasse zet het element op
              `opacity: 0` tot de waarnemer in Layout.tsx er `is-in` bij zet, en
              die waarnemer draait alleen bij een routewissel — hij ziet alleen
              wat er op dát moment staat. Deze kaart verschijnt ná het
              verzenden, op dezelfde route, en werd dus nooit zichtbaar: het
              formulier verdween en er kwam niets voor in de plaats. Het
              formulier zelf staat er bij het laden van de pagina al en mag de
              klasse wel dragen. Dezelfde keuze staat in AanvraagFormulier.tsx.
            */
            <div className="formcard formcard--contact" role="status">
              <h2>Uw bericht is verstuurd</h2>
              <p className="lede">
                Bedankt voor uw bericht. Wij nemen zo snel mogelijk contact met
                u op via het e-mailadres dat u heeft ingevuld.
              </p>
            </div>
          ) : (
            <form
              className="formcard formcard--contact reveal"
              id="contactForm"
              noValidate
              onInput={bijInvoer}
              onSubmit={(e) => void bijVerzenden(e)}
            >
              <div className="form-grid">
                <div className={fldKlasse('c6', fouten.email)}>
                  <label htmlFor="c-email">
                    Uw e-mailadres <span aria-hidden="true">*</span>
                  </label>
                  <input
                    id="c-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    aria-invalid={fouten.email ? true : undefined}
                    aria-describedby="ce-email"
                  />
                  <span className="err" id="ce-email" role="alert">
                    {fouten.email}
                  </span>
                </div>
                <div className={fldKlasse('c6', fouten.bericht)}>
                  <label htmlFor="c-bericht">
                    Uw bericht <span aria-hidden="true">*</span>
                  </label>
                  <textarea
                    id="c-bericht"
                    name="bericht"
                    required
                    maxLength={maximaleBerichtlengte}
                    className="veld--hoog"
                    aria-invalid={fouten.bericht ? true : undefined}
                    aria-describedby="ce-bericht ch-bericht"
                  ></textarea>
                  {/*
                    De grens staat erbij vóórdat iemand begint te typen. Hem pas
                    noemen bij het afkeuren betekent een lang bericht kwijtraken.
                  */}
                  <span className="hint" id="ch-bericht">
                    Maximaal {maximaleBerichtlengte} tekens. Deel hier geen
                    medische details. Die bespreekt u tijdens de afspraak.
                  </span>
                  <span className="err" id="ce-bericht" role="alert">
                    {fouten.bericht}
                  </span>
                </div>

                {/*
                  De spamval (FR-24). De controle die telt staat in
                  ContactFunction; het buiten beeld zetten gebeurt in `.spamval`
                  in site.css. Niet in een style-attribuut hier: de CSP blokkeert
                  inline styles, waardoor het veld gewoon zichtbaar werd.
                */}
                <div className="fld spamval" aria-hidden="true">
                  <label htmlFor="c-website">Website</label>
                  <input
                    id="c-website"
                    name="website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>

                {turnstileSitekey && (
                  <div className="fld c6">
                    <Turnstile
                      handvat={turnstile}
                      onToken={(token) => {
                        turnstileToken.current = token
                      }}
                    />
                  </div>
                )}

                <div
                  className={melding ? 'form-alert is-on' : 'form-alert'}
                  id="contactAlert"
                  role="alert"
                >
                  {melding}
                </div>
                <div className="form-actions">
                  <button
                    type="submit"
                    className="btn btn--primary btn--big"
                    disabled={bezig}
                  >
                    {bezig ? 'Een moment geduld…' : 'Verstuur bericht'}
                  </button>
                </div>
              </div>
            </form>
          )}

          <div className="infoband infoband--contact reveal">
            <h2>Bedrijfsgegevens</h2>
            <p>GPKeuringen · KvK: 42122048 · info@gpkeuringen.nl</p>
          </div>
        </div>
      </section>
    </>
  )
}

/** `is-error` op de veldhouder; daar hangt de rode omlijning aan. */
function fldKlasse(kolommen: string, fout: string | undefined): string {
  return fout ? `fld ${kolommen} is-error` : `fld ${kolommen}`
}

function leesVeld(form: HTMLFormElement, naam: string): string {
  const waarde = new FormData(form).get(naam)
  return typeof waarde === 'string' ? waarde : ''
}

function leesWaarden(form: HTMLFormElement): ContactFormulierWaarden {
  return {
    email: leesVeld(form, 'email').trim(),
    bericht: leesVeld(form, 'bericht').trim(),
  }
}
